import { logError } from "@/lib/error-handling";
import { apiClient } from "@/services/api";
import { AccountActivityProcessingStatusDto, AccountDto } from "@/types";
import { CreateAccountInput, UpdateAccountInput } from "@/schemas";

export type CreateAccountRequest = CreateAccountInput;
export type UpdateAccountRequest = UpdateAccountInput;

// Form data types for the create account form
export interface ParentAccountFormData {
  name: string;
  domain: string;
  purchaseDate: Date;
  owners: string[];
}

export interface AccountFormData {
  name: string;
  domain: string;
  parentAccountId?: string;
  createParentAccount?: boolean;
  parentAccount?: ParentAccountFormData;
  purchaseDate: Date;
  owners: string[];
}

/**
 * Ultra-elegant form data preparation that only includes necessary fields.
 * This approach uses object destructuring and conditional spreading for maximum clarity.
 *
 * @param formData - The form data from the create account form
 * @returns The prepared data structure for creating an account
 */
export const prepareAccountData = (
  formData: AccountFormData
): CreateAccountInput => {
  const {
    name,
    domain,
    purchaseDate,
    owners,
    createParentAccount,
    parentAccount,
    parentAccountId,
  } = formData;

  return {
    name,
    domain,
    relationshipStart: purchaseDate,
    owners: owners as [string, ...string[]], // Safe due to form validation
    // Only include parent account data when actually needed
    ...(createParentAccount &&
      parentAccount && {
        createParentAccount: true,
        parentAccount: {
          name: parentAccount.name,
          domain: parentAccount.domain,
          relationshipStart: parentAccount.purchaseDate,
          owners: parentAccount.owners as [string, ...string[]],
        },
      }),
    ...(parentAccountId && { parentAccountId }),
  };
};

/**
 * Creates a new account by sending the provided data to the backend.
 *
 * @param data - The data required to create a new account.
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function createAccount(
  data: CreateAccountInput
): Promise<AccountDto> {
  try {
    return await apiClient<AccountDto>("/api/accounts", {
      method: "POST",
      body: JSON.stringify(data),
    });
  } catch (error) {
    logError(error, "createAccount");
    throw error;
  }
}

/**
 * Fetches all accounts for the current authenticated organization.
 *
 * @returns A list of accounts with their associated owners.
 * @throws An error if the request fails.
 */
export async function fetchAccounts(): Promise<AccountDto[]> {
  try {
    const url = new URL("/api/accounts", window.location.origin);

    return await apiClient<AccountDto[]>(url.toString(), {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchAccounts");
    throw error;
  }
}

/**
 * Fetches a single account by its ID.
 *
 * @param accountId The ID of the account to fetch.
 * @returns The account data with its associated owners.
 * @throws An error if the request fails.
 */
export async function fetchAccountById(accountId: string): Promise<AccountDto> {
  try {
    return await apiClient<AccountDto>(`/api/accounts/${accountId}`, {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchAccountById");
    throw error;
  }
}

/**
 * Fetches account-level AI activity processing status.
 *
 * @param accountId The account ID to fetch processing status for.
 * @returns Current number of activities being processed by background agents.
 */
export async function fetchActivityProcessingStatus(
  accountId: string
): Promise<AccountActivityProcessingStatusDto> {
  try {
    return await apiClient<AccountActivityProcessingStatusDto>(
      `/api/accounts/${accountId}/activity-processing-status`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchActivityProcessingStatus");
    throw error;
  }
}

/**
 * Updates an existing account with the provided data.
 *
 * @param accountId The ID of the account to update.
 * @param data The updated account data.
 * @returns A promise that resolves with the updated account data.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function updateAccount(
  accountId: string,
  data: UpdateAccountRequest
): Promise<AccountDto> {
  try {
    return await apiClient<AccountDto>(`/api/accounts/${accountId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  } catch (error) {
    logError(error, "updateAccount");
    throw error;
  }
}
